import { UpdateCustomerCompanyPayload } from 'module/api/company/types';
import { FieldValueType } from 'module/api/field/types';
import { FieldType } from 'module/types/filter-field/field/field';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { getValueFormatter } from 'module/ui/components/atoms/grid/formatters/get-value-formatters';
//import { getBaseComparator } from 'module/ui/components/atoms/grid/getters/base-comparators';
import { getValueGetter } from 'module/ui/components/atoms/grid/getters/get-value-getter';
import { getKeyCreator } from 'module/ui/components/atoms/grid/key-creators/key-creator';
import { getCellEditorPopupPosition } from 'module/ui/components/atoms/grid/utils';
import { getRenderer } from 'module/ui/components/molecules/accounts/grid/acccountrender';
import { getEditor } from 'module/ui/components/molecules/field-editors/editor';
import { default_fields } from 'helpers/fields/company-fields/category';
import { CompanyFields } from 'helpers/fields/company-fields/fields';
import { startCase } from 'lodash';
import { getComparator } from './compartors';
import type { ColDef, HeaderValueGetterParams } from 'ag-grid-community';

const getHeaderName = (field: FilterableField) => {
  return field.fieldLabel ?? startCase(field.fieldName);
};

export const payloadForUpdateCompany = (
  field: FilterableField,
  value: any,
): any => {
  if (default_fields.includes(field.fieldName)) {
    return {
      [field.fieldName]: value,
    };
  }

  return {
    fields: [{ fieldId: field.fieldId, fieldValue: value }],
  };
};

const defaultColumnOrder = [
  CompanyFields.companyName,
  CompanyFields.health,
  CompanyFields.description,
  CompanyFields.stage,
  CompanyFields.nextRenewalDate,
  CompanyFields.accountOwner,
  CompanyFields.annualizedRecurringRevenue,
  'createdAt',
  'customerChampion',
  'customerMembersInvited',
  'customerMembersAccepted',
  'financialContractType',
  'estimateAtCompletion',
  'estimateToComplete',
  'actualRevenue',
  'actualCost',
  'actualProfit',
  'actualProfitMargin',
  CompanyFields.recentRiskSignals,
  CompanyFields.recentOpportunitySignals,
  CompanyFields.recentOperationalSignals,
];

const FIRST_COLUMN = defaultColumnOrder[0];

const withEditFields = (field: FilterableField): ColDef => {
  const editableFields = [
    CompanyFields.description,
    CompanyFields.accountOwner,
  ];
  const editable =
    field.type !== FieldType.Note &&
    (field.customField || editableFields.includes(field.fieldName));
  const cellEditor = editable ? getEditor(field) : null;
  const cellClass = editable
    ? ['grid-cell-editable']
    : ['grid-cell-not-editable'];

  return {
    editable: () => {
      return editable ? true : false;
    },
    cellClass,
    cellEditor,
    cellEditorPopupPosition: getCellEditorPopupPosition(field),
  };
};

export type UpdaterFunction = (params: {
  companyId: number;
  payload: UpdateCustomerCompanyPayload;
  field: FilterableField;
  from: FieldValueType;
}) => void;
type GridCellUpdateReturnType = (
  {
    companyId,
  }: {
    companyId: number;
  },
  field: FilterableField,
  from: FieldValueType,
  to: FieldValueType,
) => void;

const getGridUpdater = (mutate: UpdaterFunction): GridCellUpdateReturnType => {
  const updater: GridCellUpdateReturnType = (
    { companyId },
    field,
    from = null,
    to = null,
  ): void => {
    const isSame = from === to;

    if (isSame) {
      return;
    }
    const payload = payloadForUpdateCompany(field, to);

    if (!payload) {
      return;
    }
    mutate({ payload, companyId, field, from });
  };

  return updater;
};

/* Update mutator for cell editor function here */
const toEditParam =
  (mutate: UpdaterFunction) =>
  (def: ColDef): ColDef => ({
    ...def,
    cellEditorParams: {
      onUpdate: getGridUpdater(mutate),
    },
  });

export const withOnUpdate =
  (mutate: UpdaterFunction) =>
  (defs: ColDef[]): ColDef[] =>
    defs.map(toEditParam(mutate));

const toColumnDef = (field: FilterableField): ColDef => {
  const isSignalField =
    field.fieldName === 'recentRiskSignals' ||
    field.fieldName === 'recentOpportunitySignals' ||
    field.fieldName === 'recentOperationalSignals';

  return {
    field: field.fieldName,
    headerName: getHeaderName(field),
    headerComponent: null,
    headerValueGetter: (params: HeaderValueGetterParams) => {
      if (params.column?.getColId() === 'companyName' && params.api != null) {
        const gridApi = params.api;
        const totalRowCount = gridApi.getDisplayedRowCount();
        const groupCount = gridApi.getModel().getTopLevelRowCount();
        let dataRowCount = totalRowCount;

        if (totalRowCount !== groupCount) {
          dataRowCount = totalRowCount - groupCount;
        }
        if (dataRowCount < 0) {
          return getHeaderName(field);
        }

        return `${getHeaderName(field)} (${dataRowCount})`;
      }

      return getHeaderName(field);
    },
    headerTooltip: undefined,
    cellRenderer: getRenderer(field),
    cellRendererParams: {
      options: undefined,
    },
    menuTabs: [],
    valueGetter: isSignalField
      ? (params) => params.data
      : getValueGetter(field),
    valueFormatter: getValueFormatter(field),
    keyCreator: getKeyCreator(field),
    comparator: getComparator(field),
    sortable: true,
    lockPosition: field.fieldName === FIRST_COLUMN,
    pinned: field.fieldName === FIRST_COLUMN ? 'left' : undefined,
    suppressColumnsToolPanel: field.fieldName === FIRST_COLUMN,
    minWidth: field.fieldName === FIRST_COLUMN ? 360 : undefined,
    cellStyle: (params) => {
      if (params.colDef.field === 'companyName') {
        return { borderRight: '1px solid var(--table-border-color)' };
      }

      return undefined;
    },
    /*suppressKeyboardEvent:
      field.type === FieldType.MultiLineText
        ? suppressEnterAndTabKey
        : suppressEnterKey,*/
  };
};

export const getColDef =
  () =>
  (fields: FilterableField[]): ColDef[] =>
    fields.map((field) => ({
      ...toColumnDef(field),
      ...withEditFields(field),
    }));

export const withCustomerNameClickHandler =
  (handler?: (data) => void) =>
  (columnDefinitions: ColDef[]): ColDef[] =>
    columnDefinitions.map((column) => {
      if (column.field === CompanyFields.companyName) {
        return {
          ...column,
          cellClass: ['grid-cell-editable'],
          cellRendererParams: {
            onCustomerNameClicked: handler,
          },
        };
      }

      return column;
    });
